import { StyledElement, defineElement } from '@webtides/element-js';
import style from './tab-group.css';
import { TabSelectEvent } from '../../events/tab-select-event.js';
import TabRegisterStore from '../../stores/tab-register-store.js';

/**
 * @property {string} name - URL parameter name for deeplinking
 * @property {string} selected
 * @property {boolean} updateUrl
 * @property {TabRegisterStore} tabRegisterStore
 */
export default class TabGroup extends StyledElement {
    constructor() {
        super({ styles: [style], propertyOptions: { selected: { reflect: true } } });
    }

    properties() {
        return {
            name: 'tabs',
            selected: undefined, // 'mb-none' to not select initial tab
            updateUrl: true,
            tabRegisterStore: new TabRegisterStore(),
        };
    }

    provideProperties() {
        return {
            tabRegisterStore: this.tabRegisterStore,
        };
    }

    watch() {
        return {
            selected: () => {
                this.updateSelection();
            },
            tabRegisterStore: () => {
                this.initializeSelection();
            },
        };
    }

    initializeSelection() {
        // check if a tab was selected via url parameter
        if (this.updateUrl && this.name) {
            const urlParams = new URLSearchParams(window.location.search);
            const tabFromUrl = urlParams.get(this.name);
            if (tabFromUrl) {
                this.selectTab(tabFromUrl, true);
                return;
            }
        }

        // select first tab if none was selected via attribute
        if (this.selected !== 'mb-none' && !this.selected && this.tabRegisterStore.panels.length > 0) {
            const firstPanel = this.tabRegisterStore.panels[0];
            this.selectTab(firstPanel.getAttribute('name'), true);
        } else {
            this.updateSelection();
        }
    }

    events() {
        return {
            this: {
                // TODO: [TabSelectEvent.name] is not working with JSDoc...
                /** @param {TabSelectEvent} event */
                'mb-tab-select': (event) => {
                    this.selectTab(event.selected);
                },
                keydown: (event) => {
                    // Move focus left or right
                    if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) {
                        const links = this.tabRegisterStore.links;
                        const activeEl = links.find((t) => t.matches(':focus'));

                        if (!activeEl) return;

                        let index = links.indexOf(activeEl);

                        if (event.key === 'Home') {
                            index = 0;
                        } else if (event.key === 'End') {
                            index = links.length - 1;
                        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
                            index--;
                        } else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
                            index++;
                        }

                        if (index < 0) {
                            index = links.length - 1;
                        }

                        if (index > links.length - 1) {
                            index = 0;
                        }

                        links[index].focus({ preventScroll: true });

                        event.preventDefault();
                    }
                },
            },
            window: {
                popstate: () => {
                    if (this.updateUrl && this.name) {
                        const urlParams = new URLSearchParams(window.location.search);
                        const tabFromUrl = urlParams.get(this.name);
                        if (tabFromUrl) {
                            this.selectTab(tabFromUrl);
                        }
                    }
                },
            },
        };
    }

    updateUrlParam(selected, replace = false) {
        if (!this.name) return;
        const url = new URL(window.location);
        url.searchParams.set(this.name, selected);
        if (replace) {
            window.history.replaceState({}, '', url);
        } else {
            window.history.pushState({}, '', url);
        }
    }

    selectTab(name, replace = false) {
        const panels = Array.from(this.tabRegisterStore.panels);
        const panelExists = panels.some((panel) => panel.getAttribute('name') === name);

        if (panelExists) {
            this.selected = name;
        } else if (name !== 'mb-none' && panels.length > 0) {
            this.selected = panels[0].getAttribute('name');
            // Force replace when falling back to first panel with invalid name
            replace = true;
        }

        if (this.updateUrl && this.selected) {
            this.updateUrlParam(this.selected, replace);
        }
    }

    updateSelection() {
        for (const tabLink of this.tabRegisterStore.links) {
            tabLink.selected = tabLink.getAttribute('for') === this.selected;
        }

        for (const tabPanel of this.tabRegisterStore.panels) {
            tabPanel.selected = tabPanel.getAttribute('name') === this.selected;
        }

        for (const tabSelect of this.tabRegisterStore.selects) {
            tabSelect.selected = this.selected;
        }

        for (const tabNav of this.tabRegisterStore.navs) {
            tabNav.selected = this.selected;
        }
    }
}

defineElement('mb-tab-group', TabGroup);
